// ---------------------------------------------------------------
// IMPORTS
// ---------------------------------------------------------------
import { log, errorlog } from '../logger/logger';

// ---------------------------------------------------------------
// DEFAULTS
// ---------------------------------------------------------------
let store = {};
let fonts = {};
// Design applicable when no global values are defined.
const nodeDefaultWidth = 75;
const nodeDefaultHeight = 75;
const nodeDefaults = {
    width: nodeDefaultWidth,
    height: nodeDefaultHeight,
    fontSize: 14,
    fontColor: '#000000',
    bgColor: '#FFFFFF',
    borderStyle: 'solid',
    borderWidth: 2,
    borderColor: '#000000',
    titlePosition: 'center',
    iconOpacity: 100
};
const edgeDefaults = {
    fontSize: 13,
    fontColor: '#000000',
    borderStyle: 'dashed',
    borderWidth: 1,
    borderColor: '#000000'
};
const popupDefaults = {
    fontSize: 14,
    fontColor: '#000000',
};


// ---------------------------------------------------------------
// CLASS
// ---------------------------------------------------------------
export class Design {

    constructor() {
        this.nodeDefaults = nodeDefaults;
    }

    init(globalStore, globalFonts) {
        log('design init');
        store = globalStore;
        fonts = globalFonts;

        addDefaultDesignDataToStore()
    }

    get templates() {
        return getTemplates();
    }

    createTemplate(name, props) {
        const newId = Math.max(0, ...Object.keys(this.templates)) + 1 ;
        props['id'] = newId;
        props['name'] = name;
        this.templates.set({[newId]: props});
        return props;
    }

    updateTemplate(templateId, props) {
        Object.keys(props).forEach(key => {
          if (props[key] == null) delete props[key];
        });
        const newTemplateObj = Object.assign({}, this.templates[templateId], props);
        this.templates.set({[templateId]: newTemplateObj});
        return newTemplateObj;
    }

    getNodesWithTemplate(templateId) {
        const tempId = templateId.toString();
        return Object.keys(store.get().nodes).reduce((acc, nodeId) => {
            const node = store.get().nodes[nodeId];
            if (node.design.objectTemplate === tempId) {
                acc.push(Object.assign({'id': nodeId}, node))
            }
            return acc;
        }, []);
    }

    getEdgesWithTemplate(templateId) {
        const tempId = templateId.toString();
        return Object.keys(store.get().edges).reduce((acc, edgeId) => {
            const edge = store.get().edges[edgeId];
            if (edge.design.objectTemplate === tempId) {
                acc.push(Object.assign({'id': edgeId}, edge))
            }
            return acc;
        }, []);
    }

    forNode(nodeId, prop) {
        const val = getNodePropertyValue(nodeId, prop);
        if (val != null && val !== '') return val;
        return this.baseForNode(nodeId, prop);
    }

    forEdge(edgeId, prop) {
        const val = getEdgePropertyValue(edgeId, prop);
        if (val != null && val !== '') return val;
        return this.baseForEdge(edgeId, prop);
    }

    forPopup(prop) {
        let propValueChain = [
            getGlobalPopupPropertyValue(prop),
            getDefaultPopupPropertyValue(prop)
        ];
        return propValueChain.find(function (val) {
            return val != null && val !== '';
        });
    }

    // Base should refer to the template, global and default design settings,
    // not including the node-specific ones.
    baseForNode(nodeId, prop) {
        const templateId = getTemplateId(store.get().nodes[nodeId]);
        const propValueChain = [];
        if (!!templateId && templateId !== '0') {
            propValueChain.push(getNodeObjectTemplatePropertyValue(templateId, prop));
        }
        propValueChain.push(
            getGlobalNodePropertyValue(prop),
            getDefaultNodePropertyValue(prop)
        );
        const newVal = propValueChain.find(function (val) {
            return val != null && val !== '';
        });
        return (typeof newVal === 'undefined') ? null : newVal;
    }

    baseForEdge(edgeId, prop) {
        const templateId = getTemplateId(store.get().edges[edgeId]);
        const propValueChain = [];
        if (!!templateId && templateId !== '0') {
            propValueChain.push(getEdgeObjectTemplatePropertyValue(templateId, prop));
        }
        propValueChain.push(
            getGlobalEdgePropertyValue(prop),
            getDefaultEdgePropertyValue(prop)
        );
        const newVal = propValueChain.find(function (val) {
            return val != null && val !== '';
        });
        return (typeof newVal === 'undefined') ? null : newVal;
    }

}

// ---------------------------------------------------------------
// PRIVATE FUNCTIONS
// ---------------------------------------------------------------
function addDefaultDesignDataToStore() {
    const data = {
        nodes: {},
        edges: {},
        popups: {
            detached: {},
            small: {},
            large: {},
            marginal: {},
            nav: {}
        },
        templates: {}
    };
    store.get().design.set(data);
}

function getDefaultNodeFontFamily() {
    const nodeContentFont = store.get().map.fonts.nodeContent;
    return (nodeContentFont) ? nodeContentFont : fonts.get()[0].family;
}
function getDefaultEdgeFontFamily() {
    const edgeContentFont = store.get().map.fonts.edgeContent;
    return (edgeContentFont) ? edgeContentFont : fonts.get()[0].family;
}

function getTemplates() {
    return store.get().design.templates;
}

function getTemplate(id) {
    return getTemplates()[id];
}

function getTemplateId(entity) {
    if (!entity) return;
    return entity.design.objectTemplate;
}

function resolve(path, obj) {
    const properties = Array.isArray(path) ? path : path.split('.');
    return properties.reduce((prev, curr) => prev && prev[curr], obj);
}

const NODE_PROPERTY_MAP = {
    'height': 'measurement.height',
    'width': 'measurement.width',
    'icon': 'content.icon'
};

function getNodePropertyValue(nodeId, prop) {
    const nodeObj = store.get().nodes[nodeId];
    // No node info, nothing to look up.
    if (!nodeObj) return;
    // Find the right nested object to look up.
    let property_path;
    if (NODE_PROPERTY_MAP.hasOwnProperty(prop)) {
        property_path = NODE_PROPERTY_MAP[prop];
    } else {
        property_path = 'design.' + prop;
    }
    return resolve(property_path, nodeObj);
}

function getNodeObjectTemplatePropertyValue(templateId, prop) {
    const template = getTemplates()[templateId];
    if (!template) return;
    return template[prop];
}

function getGlobalNodePropertyValue(prop) {
    return store.get().design.nodes[prop];
}

function getDefaultNodePropertyValue(prop) {
    return (prop === 'fontFamily') ? getDefaultNodeFontFamily() : nodeDefaults[prop];
}

function getEdgePropertyValue(edgeId, prop) {
    const edgeObj = store.get().edges[edgeId];
    // No edge info, nothing to look up.
    if (!edgeObj) return;
    // Get prop value
    return edgeObj['design'][prop];
}

function getEdgeObjectTemplatePropertyValue(templateId, prop) {
    const template = getTemplates()[templateId];
    if (!template) return;
    return template[prop];
}

function getGlobalEdgePropertyValue(prop) {
    return store.get().design.edges[prop];
}

function getDefaultEdgePropertyValue(prop) {
    return (prop === 'fontFamily') ? getDefaultEdgeFontFamily() : edgeDefaults[prop];
}

function getGlobalPopupPropertyValue(prop) {
    return store.get().design.popups[prop];
}

function getDefaultPopupPropertyValue(prop) {
    return (prop === 'fontFamily') ? fonts.get()[0].family : popupDefaults[prop];
}
